// FILE: rating-tabs.tsx
// VERSION: 3.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Секция «Отзывы» на карточке /rating/company/[id]: сводные звёзды с
//          дробным заполнением, распределение по звёздам, список одобренных
//          отзывов (роль, дата, ответ организации) и форма отправки отзыва.
//          Форма переиспользует ChopReviewForm (единый API-контракт /api/seo/review).
// SCOPE: [rating/company/[id], chop_reviews]
// KEYWORDS: DOMAIN(seo); CONCEPT(reviews); TECH(react)
// END_MODULE_CONTRACT

"use client";

import React, { useState } from "react";
import { Star, MessageSquare } from "lucide-react";
import { ChopReviewForm } from "@/features/seo-portal/components/chop-review-form";

// START_INTERFACE_OxpaReview
interface OxpaReview {
  id: string;
  author_name: string | null;
  author_role: string | null;
  rating: number;
  content: string | null;
  owner_reply?: string | null;
  created_at: string;
}
// END_INTERFACE_OxpaReview

// START_INTERFACE_RatingTabsProps
interface RatingTabsProps {
  /** Одобренные отзывы (status=approved), свежие сверху */
  oxpaReviews: OxpaReview[];
  /** Название компании (name_short) */
  companyName: string;
  /** UUID компании — chop_id для формы отзыва */
  companyId: string;
  /** Распределение по звёздам [{ rating: 5, count: n }...] — server-side агрегат по approved */
  distribution: { rating: number; count: number }[];
  /** Средний рейтинг из seo_companies.rating (null, если отзывов нет) */
  averageRating: number | null;
  /** Общее число одобренных отзывов из seo_companies.reviews_count */
  totalCount: number;
}
// END_INTERFACE_RatingTabsProps

// START_FUNCTION_getRoleLabel
function getRoleLabel(role: string | null): string {
  const map: Record<string, string> = {
    employee: "Сотрудник",
    client: "Клиент",
    partner: "Партнёр",
    anonymous: "Аноним",
  };
  return role ? (map[role] ?? "Аноним") : "Аноним";
}
// END_FUNCTION_getRoleLabel

// START_FUNCTION_getReviewsWord
function getReviewsWord(count: number): string {
  const lastDigit = count % 10;
  const lastTwo = count % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return "отзывов";
  if (lastDigit === 1) return "отзыв";
  if (lastDigit >= 2 && lastDigit <= 4) return "отзыва";
  return "отзывов";
}
// END_FUNCTION_getReviewsWord

// START_FUNCTION_formatDate
/** Дата в формате ДД.ММ.ГГГГ (бренд: mono-числа) */
function formatDate(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
}
// END_FUNCTION_formatDate

// START_COMPONENT_StarGlyph
/** Один SVG-глиф звезды. Цвет — через currentColor. */
function StarGlyph({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 2.5l2.95 5.98 6.6.96-4.78 4.65 1.13 6.58L12 17.57l-5.9 3.1 1.13-6.58-4.78-4.65 6.6-.96L12 2.5z"
        fill="currentColor"
      />
    </svg>
  );
}
// END_COMPONENT_StarGlyph

// START_COMPONENT_FractionalStars
/**
 * Звёзды 1–5 с дробным заполнением: янтарный слой обрезается по ширине
 * пропорционально рейтингу (4.6 → 92% ширины). Бренд: янтарь (--portal-warn).
 */
function FractionalStars({ value, size = 28 }: { value: number; size?: number }) {
  const clamped = Math.max(0, Math.min(5, value));
  const pct = (clamped / 5) * 100;
  const glyphs = [1, 2, 3, 4, 5].map((s) => (
    <StarGlyph key={s} size={size} />
  ));
  return (
    <div
      className="relative inline-flex shrink-0"
      role="img"
      aria-label={`Рейтинг ${clamped.toFixed(1)} из 5`}
    >
      {/* Слой пустых звёзд */}
      <div className="flex gap-0.5 text-[hsl(var(--portal-text-dim))]">{glyphs}</div>
      {/* Слой залитых звёзд, обрезанный по ширине */}
      <div
        className="absolute inset-y-0 left-0 overflow-hidden flex gap-0.5 text-[hsl(var(--portal-warn))]"
        style={{ width: `${pct}%` }}
        aria-hidden="true"
      >
        {glyphs}
      </div>
    </div>
  );
}
// END_COMPONENT_FractionalStars

// START_COMPONENT_SmallStars
/** Компактные звёзды отзыва (у каждого отзыва целая оценка 1-5). */
function SmallStars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`Оценка ${rating} из 5`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-3.5 h-3.5 ${
            s <= rating
              ? "fill-[hsl(var(--portal-warn))] text-[hsl(var(--portal-warn))]"
              : "fill-transparent text-[hsl(var(--portal-text-dim))]"
          }`}
        />
      ))}
    </div>
  );
}
// END_COMPONENT_SmallStars

// START_COMPONENT_ReviewCard
/**
 * Карточка отзыва: автор, бейдж роли, дата ДД.ММ.ГГГГ, звёзды,
 * текст с line-clamp-3 + «Показать полностью», блок «Ответ организации».
 */
function ReviewCard({ review }: { review: OxpaReview }) {
  const [expanded, setExpanded] = useState(false);
  const content = review.content ?? "";
  const isLong = content.length > 220;

  return (
    <article className="bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] p-5 hover:border-[hsl(var(--portal-border-hover))] transition-colors">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-bold text-[hsl(var(--portal-text))] text-sm truncate">
              {review.author_name || "Аноним"}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 border border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest">
              {getRoleLabel(review.author_role)}
            </span>
          </div>
          <span className="text-[11px] text-[hsl(var(--portal-text-dim))] font-mono tabular-nums">
            {formatDate(review.created_at)}
          </span>
        </div>
        <SmallStars rating={review.rating} />
      </div>

      {content && (
        <>
          <p
            className={`text-[hsl(var(--portal-text-secondary))] text-sm font-sans leading-relaxed whitespace-pre-wrap break-words ${
              isLong && !expanded ? "line-clamp-3" : ""
            }`}
          >
            {content}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold text-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent-hover))] transition-colors"
            >
              {expanded ? "Свернуть" : "Показать полностью"}
            </button>
          )}
        </>
      )}

      {review.owner_reply && (
        <div className="mt-4 pl-4 border-l-2 border-[hsl(var(--portal-border-hover))]">
          <div className="flex items-center gap-1.5 mb-1">
            <MessageSquare className="w-3 h-3 text-[hsl(var(--portal-accent))]" />
            <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
              Ответ организации
            </span>
          </div>
          <p className="text-[hsl(var(--portal-text-secondary))] text-sm font-sans leading-relaxed whitespace-pre-wrap break-words">
            {review.owner_reply}
          </p>
        </div>
      )}
    </article>
  );
}
// END_COMPONENT_ReviewCard

// START_COMPONENT_RatingTabs
/**
 * Секция «Отзывы» карточки компании: сводные звёзды + распределение + список + форма.
 */
export function RatingTabs({
  oxpaReviews,
  companyName,
  companyId,
  distribution,
  averageRating,
  totalCount,
}: RatingTabsProps) {
  const distMap = new Map(distribution.map((d) => [d.rating, d.count]));
  const maxCount = Math.max(1, ...distribution.map((d) => d.count));
  const avg = averageRating ?? 0;

  return (
    <section aria-labelledby="reviews-heading">
      <h2 id="reviews-heading" className="text-xl md:text-2xl font-black uppercase tracking-tight mb-6">
        Отзывы
      </h2>

      {/* Шапка секции: сводные звёзды + число рейтинга + количество */}
      <div className="border border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-bg))] p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          {totalCount > 0 && (
            <>
              <FractionalStars value={avg} size={30} />
              <div className="text-4xl font-black font-mono tabular-nums text-[hsl(var(--portal-text))] leading-none">
                {avg.toFixed(1)}
              </div>
            </>
          )}
          <div className="text-sm text-[hsl(var(--portal-text-muted))] font-sans">
            ({totalCount} {getReviewsWord(totalCount)})
          </div>
        </div>

        {/* Распределение по звёздам 5..1 */}
        {totalCount > 0 && (
          <div className="mt-6 space-y-1.5">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = distMap.get(star) ?? 0;
              return (
                <div key={star} className="flex items-center gap-3">
                  <span className="w-8 text-right text-xs font-mono tabular-nums text-[hsl(var(--portal-text-muted))] shrink-0">
                    {star} ★
                  </span>
                  <div className="flex-1 h-2 bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))]">
                    <div
                      className="h-full bg-[hsl(var(--portal-warn))]"
                      style={{ width: `${(count / maxCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-xs font-mono tabular-nums text-[hsl(var(--portal-text-secondary))] shrink-0">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Список отзывов / EmptyState */}
      {oxpaReviews.length === 0 ? (
        <div className="border-2 border-dashed border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-bg))]/50 p-12 text-center flex flex-col items-center justify-center mb-6">
          <div className="bg-[hsl(var(--portal-surface))] w-16 h-16 flex items-center justify-center mb-4 border border-[hsl(var(--portal-border-hover))]">
            <Star className="w-8 h-8 text-[hsl(var(--portal-text-dim))]" />
          </div>
          <h3 className="text-lg font-bold font-mono uppercase text-[hsl(var(--portal-text))] mb-2">
            Пока нет отзывов
          </h3>
          <p className="text-[hsl(var(--portal-text-muted))] font-sans text-sm max-w-md mb-5">
            Оставьте первый отзыв о компании «{companyName}».
          </p>
          <a
            href="#review-form"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] transition-colors text-xs uppercase tracking-wider font-bold"
          >
            Оставить отзыв
          </a>
        </div>
      ) : (
        <div className="space-y-4 mb-6">
          {oxpaReviews.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>
      )}

      {/* Форма отправки отзыва (переиспользуемая, отзыв создаётся со статусом pending) */}
      <div id="review-form" className="scroll-mt-8">
        <ChopReviewForm companyId={companyId} />
      </div>

      <div className="mt-8 text-[10px] text-[hsl(var(--portal-text-dim))] text-center uppercase tracking-widest border-t border-[hsl(var(--portal-border))] pt-6">
        Все отзывы проходят модерацию. OXPA.PRO не удаляет негативные отзывы по запросу компаний.
      </div>
    </section>
  );
}
// END_COMPONENT_RatingTabs
